import { DocumentTextIcon, ExclamationTriangleIcon, ArrowPathIcon, DocumentArrowDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "components/common/LoadingSpinner";
import { Template } from "types/template";
import SelectedDocument from "../SelectedDocument";
import DocumentPreview from "../DocumentPreview";
import { useLanguage } from "contexts/LanguageContext";
import { Customer } from "types/customer";
import { Quotation } from "types/quotation";
import { useState, useRef, useEffect, useMemo } from "react";
import { renderAsync } from 'docx-preview';
import toast from "react-hot-toast";
import { isQuotation } from "../DocumentGeneratorModal";
import { useTemplates } from "hooks/useTemplates";

interface ChooseTemplatePageProps {
  documentDataProvider: Quotation | Customer | null;
  templates: Template[];
  selectedTemplate: Template | null;
  loading?: boolean;
  handleTemplateSelect: (template: Template) => void;
  templateSearchQuery: string;
  setTemplateSearchQuery: (query: string) => void;
  templateFields: string[];
  isLoadingPreview: boolean;
  setIsLoadingPreview: (value: boolean) => void;
  forceContractTemplates?: boolean;
}

const ChooseTemplatePage = ({
  documentDataProvider,
  templates,
  selectedTemplate,
  loading = false,
  handleTemplateSelect,
  templateSearchQuery,
  setTemplateSearchQuery,
  templateFields,
  isLoadingPreview,
  setIsLoadingPreview,
  forceContractTemplates = false
}: ChooseTemplatePageProps) => {
  const { t } = useLanguage();
  // Default to contract templates if forced, otherwise use appropriate type based on document provider
  const [templateType, setTemplateType] = useState<'quotation' | 'contract'>(
    forceContractTemplates ? 'contract' : (documentDataProvider && isQuotation(documentDataProvider) ? 'quotation' : 'contract')
  );
  const { getTemplateByType } = useTemplates();
  const [localTemplates, setLocalTemplates] = useState<Template[]>(templates);

  // Azonnal betöltjük a szerződés sablonokat a komponens rendereléskor
  useEffect(() => {
    const loadContractTemplates = async () => {
      if (!documentDataProvider) return;
      console.log("Initial loading of contract templates");
      const templates = await getTemplateByType(forceContractTemplates ? 'contract' : templateType);
      console.log("Initial contract templates:", templates);
      setLocalTemplates(templates);
    };
    loadContractTemplates();
  }, [getTemplateByType, documentDataProvider, forceContractTemplates, templateType]);

  // Fetch templates when template type changes
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!documentDataProvider) return;
      console.log("Fetching templates for type:", forceContractTemplates ? 'contract' : templateType);
      const templates = await getTemplateByType(forceContractTemplates ? 'contract' : templateType);
      console.log("Templates fetched:", templates);
      setLocalTemplates(templates);
    };
    fetchTemplates();
  }, [templateType, getTemplateByType, documentDataProvider, forceContractTemplates]);

  // Rendezett sablonok a kereső alapján
  const filteredTemplates = useMemo(() => {
    // Először szűrjük a sablonokat típus szerint
    const filteredByType = localTemplates.filter(template => template.type === templateType);
    console.log("Filtering templates by type:", templateType, "Found:", filteredByType.length);
    
    // Majd szűrjük a keresési feltétel alapján
    const filtered = filteredByType.filter(template =>
      template.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(templateSearchQuery.toLowerCase())
    );
    
    console.log("After search filter:", filtered.length);
    return filtered;
  }, [localTemplates, templateSearchQuery, templateType]);

  if (!documentDataProvider) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <DocumentTextIcon className="h-12 w-12 text-gray-500 mb-4" />
        <p className="text-gray-400 text-sm text-center">
          {t('documents.selectDocument')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-6 h-auto md:h-[650px]">
      {/* Bal oldal: Sablon választás - Mobile-first */}
      <div className="bg-white rounded-lg p-3 sm:p-5 border border-gray-200 md:w-1/2 w-full shadow-sm">
        <SelectedDocument documentDataProvider={documentDataProvider} />
        <div className="flex flex-col mb-3 sm:mb-5 gap-2">
          <div>
            <h3 className="text-sm font-medium text-gray-800 mb-1">
              {t('documents.selectTemplate')}
            </h3>
            <p className="text-xs text-gray-500">
              {t('documents.selectTemplateDescription')}
            </p>
          </div>
        </div>

        {/* Search box */}
        <div className="relative mb-4">
          <div className="flex items-center bg-gray-100 rounded-md px-3 py-2">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-500 mr-2" />
            <input
              type="text"
              placeholder={t('documents.searchTemplate')}
              className="bg-transparent border-0 text-gray-800 placeholder-gray-500 focus:ring-0 w-full"
              value={templateSearchQuery}
              onChange={e => setTemplateSearchQuery(e.target.value)}
            />
            {templateSearchQuery && (
              <button
                onClick={() => setTemplateSearchQuery('')}
                className="text-gray-500 hover:text-gray-700"
              >
                <span className="sr-only">Clear</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1.5 ml-1">
            {filteredTemplates.length > 0 && (
              <span>{t('documents.templateCount', { count: filteredTemplates.length })}</span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-6 sm:py-10">
            <LoadingSpinner size="md" color="teal-400" />
          </div>
        ) : localTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 sm:py-10 text-center">
            <DocumentTextIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-3" />
            <p className="text-gray-500 mb-1">
              {t('documents.noTemplatesAvailable')}
            </p>
            <p className="text-gray-500 text-xs sm:text-sm">
              Tölts fel sablonokat a dokumentumok generálásához
            </p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center p-8 bg-gray-50 rounded-lg">
            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-600">{t('documents.noSearchResults')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('documents.tryDifferentSearch')}</p>
          </div>
        ) : (
          <>
            {/* Sablon Lista - Mindig látható */}
            <div className="mb-6 max-h-[350px] overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-teal-500/50">
              <div className="space-y-2">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className={`relative cursor-pointer select-none py-3 px-4 rounded-lg border ${
                      selectedTemplate?.id === template.id
                        ? 'bg-teal-50 border-teal-300 shadow-sm'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-1.5 rounded ${selectedTemplate?.id === template.id ? 'bg-teal-200' : 'bg-gray-200'}`}>
                        <DocumentTextIcon className="h-4 w-4 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{template.name}</div>
                        <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {template.description || "Nincs leírás"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Kiválasztott sablon információk */}
            {selectedTemplate && (
              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3 sm:mb-4">
                  <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 text-teal-600 mr-2" />
                  <h4 className="text-sm sm:text-base font-medium text-gray-800">{t('documents.selectedTemplate')}</h4>
                </div>

                <div className="text-sm sm:text-base font-medium text-gray-800 mb-1">{selectedTemplate.name}</div>
                <div className="text-xs sm:text-sm text-gray-600 mb-3">{selectedTemplate.description || "Nincs leírás"}</div>

                {templateFields.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="text-[10px] font-medium text-gray-700 mb-2">{t('documents.fieldsInTemplate', { count: templateFields.length })}</div>
                    
                    {/* Fontos mezők szekció */}
                    <div className="flex flex-wrap gap-0.5 mb-2">
                      {templateFields
                        .filter(field => [
                          'Ajánlat száma',
                          'Ajánlat érvényessége',
                          'Ajánlati ár',
                          'Ajánlati ár HUF/kWh-EUR/ MWh',
                          'Cég adószáma'
                        ].includes(field))
                        .map((field: string) => (
                          <span key={field} className="inline-flex items-center px-1 py-0.5 rounded-sm text-[9px] bg-blue-50 text-blue-700 border border-blue-100 font-medium whitespace-nowrap">
                            {field}
                          </span>
                        ))
                      }
                    </div>
                    
                    {/* További mezők szekció - csak egy elválasztó vonal */}
                    <div className="w-full h-px bg-gray-100 my-1.5"></div>
                    
                    <div className="flex flex-wrap gap-0.5">
                      {templateFields
                        .filter(field => ![
                          'Ajánlat száma',
                          'Ajánlat érvényessége',
                          'Ajánlati ár',
                          'Ajánlati ár HUF/kWh-EUR/ MWh',
                          'Cég adószáma'
                        ].includes(field))
                        .map((field: string) => (
                          <span key={field} className="inline-flex items-center px-1 py-0.5 rounded-sm text-[9px] bg-gray-50 text-gray-600 border border-gray-100 whitespace-nowrap">
                            {field}
                          </span>
                        ))
                      }
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Jobb oldal: Sablon előnézet */}
      <div className="md:w-1/2 w-full flex flex-col bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm">
        <DocumentPreview
          selectedTemplate={selectedTemplate}
          isLoadingPreview={isLoadingPreview}
          setIsLoadingPreview={setIsLoadingPreview}
        />
      </div>
    </div>
  )
}

export default ChooseTemplatePage;  